import React from 'react';
import { Shield, AlertTriangle, CheckCircle, MapPin, User, Calendar, Info, BadgeCheck, X } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент проверки приватности EXIF-данных
 */
const ExifPrivacyChecker = ({ exifData }) => {
  if (!exifData) return null;
  
  // Проверка наличия GPS-координат
  const hasLocation = exifData.location && exifData.location.latitude !== null && exifData.location.longitude !== null;
  
  // Проверка наличия персональных данных
  const hasPersonalData = 
    exifData.author || 
    exifData.camera.lensSerialNumber ||
    hasLocation || 
    exifData.privacy.hasPersonalData;
  
  // Определение общего уровня риска
  const privacyRiskLevel = hasLocation ? "Высокий" : hasPersonalData ? "Средний" : "Низкий";
  
  // Цвета для уровней риска
  const riskColors = {
    "Высокий": {
      bgLight: "bg-red-50",
      bgDark: "dark:bg-red-900/20",
      borderLight: "border-red-100",
      borderDark: "dark:border-red-800",
      textLight: "text-red-800",
      textDark: "dark:text-red-300"
    },
    "Средний": {
      bgLight: "bg-amber-50",
      bgDark: "dark:bg-amber-900/20",
      borderLight: "border-amber-100",
      borderDark: "dark:border-amber-800",
      textLight: "text-amber-800",
      textDark: "dark:text-amber-300"
    },
    "Низкий": {
      bgLight: "bg-green-50",
      bgDark: "dark:bg-green-900/20",
      borderLight: "border-green-100",
      borderDark: "dark:border-green-800",
      textLight: "text-green-800",
      textDark: "dark:text-green-300"
    }
  };
  
  // Рекомендации по приватности
  const recommendations = [
    ...(hasLocation ? [
      "Удалите GPS-координаты перед публикацией изображения в интернете",
      "Проверьте настройки вашей камеры/смартфона для отключения геотегирования будущих снимков"
    ] : []),
    ...(exifData.author ? [
      "Имя автора указано в метаданных, что может раскрывать вашу личность"
    ] : []),
    ...(exifData.camera.lensSerialNumber ? [
      "Серийный номер объектива может быть использован для идентификации вашего оборудования"
    ] : []),
    ...(exifData.date.original ? [
      "Дата и время съемки могут раскрыть ваше местоположение в определенный момент времени"
    ] : []),
    ...(exifData.privacy.recommendations || [])
  ];
  
  // Если рекомендаций нет, добавляем базовые
  if (recommendations.length === 0) {
    recommendations.push(
      "Регулярно проверяйте EXIF-данные перед публикацией фотографий",
      "Используйте инструменты для удаления метаданных перед публикацией в интернете",
      "Обновляйте программное обеспечение камеры для исправления уязвимостей"
    );
  }

  // Функция для очистки всех потенциально опасных данных
  const handleRemovePrivateData = () => {
    alert("В реальном приложении здесь была бы функция удаления метаданных из изображения. Для полноценного удаления требуется серверная обработка или использование WebAssembly библиотек.");
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
        <Shield className="w-5 h-5 mr-2 text-purple-500" />
        Проверка приватности EXIF-данных
      </h2>
      
      <p className="text-gray-600 dark:text-gray-400 mb-6">
        EXIF-данные могут содержать личную информацию, включая точные координаты съемки, 
        сведения о вашем устройстве и другие метаданные, которые могут нарушить вашу приватность.
      </p>

      {/* Общий уровень риска */}
      <div className={`p-4 mb-6 rounded-lg ${riskColors[privacyRiskLevel].bgLight} ${riskColors[privacyRiskLevel].bgDark} 
        border ${riskColors[privacyRiskLevel].borderLight} ${riskColors[privacyRiskLevel].borderDark}`}>
        <div className="flex items-start">
          {privacyRiskLevel === "Низкий" ? (
            <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
          ) : (
            <AlertTriangle className="w-5 h-5 text-amber-500 mr-3 flex-shrink-0 mt-0.5" />
          )}
          <div>
            <h3 className={`text-sm font-medium mb-1 ${riskColors[privacyRiskLevel].textLight} ${riskColors[privacyRiskLevel].textDark}`}>
              Уровень риска для приватности: {privacyRiskLevel}
            </h3>
            <p className={`text-sm ${riskColors[privacyRiskLevel].textLight} ${riskColors[privacyRiskLevel].textDark} opacity-90`}>
              {privacyRiskLevel === "Высокий" ? 
                "Обнаружены потенциально конфиденциальные данные, которые рекомендуется удалить." :
                privacyRiskLevel === "Средний" ? 
                "Обнаружены некоторые метаданные, которые могут представлять риск для приватности." :
                "Критических данных для приватности не обнаружено, но всегда рекомендуется проверять метаданные перед публикацией."}
            </p>
          </div>
        </div>
      </div>

      {/* Результаты проверки приватности */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="space-y-4">
          <h3 className="text-base font-medium text-gray-900 dark:text-white mb-2">
            Обнаруженные данные
          </h3>

          {/* GPS-координаты */}
          <PrivacyItem 
            title="GPS-координаты" 
            status={hasLocation}
            icon={<MapPin className="w-4 h-4" />}
            details={hasLocation ? `${exifData.location.latitude.toFixed(6)}, ${exifData.location.longitude.toFixed(6)}` : "Не обнаружены"}
            riskLevel={hasLocation ? "Высокий" : "Нет"}
          />

          {/* Персональные данные */}
          <PrivacyItem 
            title="Данные автора" 
            status={!!exifData.author}
            icon={<User className="w-4 h-4" />}
            details={exifData.author || "Не обнаружены"}
            riskLevel={!!exifData.author ? "Средний" : "Нет"}
          />

          {/* Дата и время */}
          <PrivacyItem 
            title="Дата и время съемки" 
            status={!!exifData.date.original}
            icon={<Calendar className="w-4 h-4" />}
            details={exifData.date.original ? new Date(exifData.date.original).toLocaleString('ru-RU') : "Не обнаружены"}
            riskLevel="Низкий"
          />

          {/* Серийные номера устройств */}
          <PrivacyItem 
            title="Серийные номера устройств" 
            status={!!exifData.camera.lensSerialNumber}
            icon={<Info className="w-4 h-4" />}
            details={exifData.camera.lensSerialNumber ? `Серийный номер объектива: ${exifData.camera.lensSerialNumber}` : "Не обнаружены"}
            riskLevel={!!exifData.camera.lensSerialNumber ? "Средний" : "Нет"}
          />
        </div>

        <div>
          <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4">
            Рекомендации по защите данных
          </h3>

          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-100 dark:border-blue-800">
            <ul className="space-y-3">
              {recommendations.map((recommendation, index) => (
                <li key={index} className="flex items-start">
                  <BadgeCheck className="w-4 h-4 text-blue-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span className="text-sm text-blue-700 dark:text-blue-300">{recommendation}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-4">
            <Button 
              onClick={handleRemovePrivateData}
              primary
              fullWidth
              icon={<Shield className="w-4 h-4" />}
            >
              Удалить личные данные из изображения
            </Button>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
              Эта операция необратима и удалит все потенциально конфиденциальные метаданные.
            </p>
          </div>
        </div>
      </div>

      {/* Информация о российских законах */}
      <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-5 border border-gray-200 dark:border-gray-700">
        <h3 className="text-base font-medium text-gray-900 dark:text-white mb-3">
          Защита персональных данных в России
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
          Согласно Федеральному закону "О персональных данных" (№152-ФЗ), к персональным данным относится любая 
          информация, которая прямо или косвенно может идентифицировать физическое лицо. 
          EXIF-данные, содержащие GPS-координаты, могут раскрыть местоположение человека и поэтому требуют защиты.
        </p>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Роскомнадзор рекомендует удалять метаданные из фотографий перед их публикацией в интернете, 
          особенно если эти фотографии сделаны рядом с местом вашего проживания или содержат информацию о несовершеннолетних.
        </p>
      </div>
    </div>
  );
};

/**
 * Компонент отображения элемента проверки приватности
 */
const PrivacyItem = ({ title, status, icon, details, riskLevel }) => {
  // Цвета в зависимости от уровня риска
  let colorClasses = "text-green-500";
  if (riskLevel === "Высокий") colorClasses = "text-red-500";
  else if (riskLevel === "Средний") colorClasses = "text-amber-500";
  
  return (
    <div className="flex items-start p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700">
      <div className={`mr-3 flex-shrink-0 mt-0.5 ${colorClasses}`}>
        {icon}
      </div>
      <div className="flex-grow">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white">{title}</h4>
          {status ? (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300">
              Обнаружено
            </span>
          ) : (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300">
              Не обнаружено
            </span>
          )}
        </div>
        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{details}</p>
        {riskLevel !== "Нет" && (
          <div className="mt-2 flex items-center">
            <span className="text-xs text-gray-500 dark:text-gray-400 mr-2">Уровень риска:</span>
            <span className={`text-xs font-medium ${
              riskLevel === "Высокий" 
                ? "text-red-600 dark:text-red-400" 
                : riskLevel === "Средний"
                  ? "text-amber-600 dark:text-amber-400"
                  : "text-green-600 dark:text-green-400"
            }`}>
              {riskLevel}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExifPrivacyChecker;